import './styles.scss';
import Moment from 'react-moment';
import CopyToClipboard from 'react-copy-to-clipboard';
import Parser from 'html-react-parser';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { useSelector } from 'react-redux';
import l from '../../../../lang';
import ChipStatus from '../../../common/chip-status';
import ChargebackResolution from '../../../common/chargeback-resolution';
import { Urls } from '../../../../config/urls';
import { createDate, getLanguage, getMappedCountryCode, openNewTabAndFocus } from '../../../../utils';
import { useTransactions } from '../../../../store/transactions';
import { canRequestRefund } from '../../../../store/transactions/roles';
import { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import Loading from '../../../common/loading';
import Card from '../../../common/card';
import { ReactComponent as CopyIcon } from '../../../../assets/icons/copy.svg';
import { ReactComponent as CardIcon } from '../../../../assets/icons/card.svg';
import { ReactComponent as DollarIcon } from '../../../../assets/icons/dollar.svg';
import { ReactComponent as UserIcon } from '../../../../assets/icons/user-circle.svg';
import { ReactComponent as ArrowRightIcon } from '../../../../assets/icons/arrow-right-completed.svg';
import { Tooltip } from '../../../common/tooltip';
import { PaymentMethodTypes, PaymentStatus, SHOPIFY, SHOPIFY_TRANSPARENT } from '../../../../constants';
import { useDashboard } from '../../../../store/dashboard';
import { Button } from '../../../common/button';
import { Transaction } from '../../../../store/transactions/interfaces';
import Dialog from '../../../common/dialog';
import AddRefund from '../../refunds/add-refund';
import { useRefunds } from '../../../../store/refunds';
import { Routes } from '../../../../config/routes';
import TopDashboardSection from '../../../common/top-dashboard-section';
import ButtonLink from '../../../common/button-link';
import { ReactComponent as BackIcon } from '../../../../assets/icons/back.svg';
import MessageError from '../../../common/message-error';
import { ReactComponent as InfoIcon } from '../../../../assets/icons/info-unfilled.svg';
import TooltipIcon from '../../../common/tooltip-icon';
import { ReactComponent as RefundsIcon } from '../../../../assets/icons/refunds.svg';
import { ReactComponent as LinkIcon } from '../../../../assets/icons/link.svg';
import { ReactComponent as DeclinedWarningIcon } from '../../../../assets/icons/warning-alert.svg';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckIcon from '@mui/icons-material/CheckCircle';
import { functionalCurrenciesByCountry } from '../../../../config';
import {
  segmentTransactionsTransactionDetailCopyExternalId,
  segmentTransactionsTransactionDetailCopyInvoiceId,
} from '../../../../utils/analytics';
import { getEciBrandResult, ECI_RESULT } from '../../../../utils/transactions-utils';
import RejectionReasonModal from './rejection-reason-modal';
import TransactionDetailSection from './transaction-detail-section';

const getEciTooltipContent = (code: string) => {
  const titleKey = `transactions.eciCode.${code}.title`;
  const descriptionKey = `transactions.eciCode.${code}.description`;

  const title = l(titleKey);
  const description = l(descriptionKey);

  if (title === titleKey || description === descriptionKey) {
    return null;
  }

  return (
    <div>
      <strong>{title}</strong>
      <div>{description}</div>
    </div>
  );
};

const getStatusDetails = (
  status: PaymentStatus | undefined,
  refunded: boolean,
  showReviewStatus: boolean,
  eciResult: string | null
) => {
  const statusKey = refunded ? 'REFUNDED' : showReviewStatus ? 'DECLINED' : status ?? 'DECLINED';
  const statusText = l(`transactions.details.statusDetails.${statusKey}`);
  return eciResult === ECI_RESULT.FAILURE ? `${statusText} (3DS)` : statusText;
};

const getNextAction = (endUserErrorType?: string) => {
  if (!endUserErrorType) {
    return l('transactions.details.nextAction.none');
  }

  const translationKey = `checkout-logs.type.${endUserErrorType}`;
  const translation = l(translationKey);

  return translation === translationKey ? l('transactions.details.nextAction.none') : translation;
};

const getNeedsAttention = (endUserErrorType?: string) => {
  if (!endUserErrorType) {
    return l('transactions.details.nextAction.none');
  }

  const translationKey = `checkout-logs.type.${endUserErrorType}`;
  const translation = l(translationKey);

  return translation === translationKey ? l('transactions.details.nextAction.none') : translation;
};

const getDeclinedHelpUrl = () => {
  const lang = getLanguage();

  switch (lang) {
    case 'es':
      return Urls.checkoutLogsHelp.es;
    case 'en':
      return Urls.checkoutLogsHelp.en;
    case 'pt':
      return Urls.checkoutLogsHelp.pt;
    default:
      return Urls.checkoutLogsHelp.en;
  }
};

const TransactionDetail = () => {
  let { invoice_id: invoiceId } = useParams<{ invoice_id: string }>();
  const { merchantMe } = useDashboard();
  const { allTransactions, transactionDetail, setTransactionDetail, getTransactionDetail } = useTransactions();
  const { auth } = useSelector(state => state);
  const canRefund = canRequestRefund(auth?.roles);
  const { loading, data, error, errorInfo } = transactionDetail;
  const [wasCopied, setWasCopied] = useState(false);
  const [transactionToRefund, setTransactionToRefund] = useState<Transaction | null>();
  const [showRejectionReasonModal, setShowRejectionReasonModal] = useState(false);

  const history = useHistory();
  const {
    requestRefund: { success: requestRefundSucess },
  } = useRefunds();

  const isCrossBorder = merchantMe?.country !== transactionDetail.data?.country;

  useEffect(() => {
    const trans = allTransactions?.data.find(t => t.invoiceId === invoiceId);
    if (trans?.invoiceId === invoiceId) {
      setTransactionDetail(trans);
    }
    getTransactionDetail(invoiceId);
  }, [invoiceId]);

  const exportToPDF = async () => {
    const fieldsWithEffect = document.querySelectorAll('.transaction_detail__box_data__value__text');
    const originalStyles = Array.from(fieldsWithEffect).map(field => field.getAttribute('style') || '');
    fieldsWithEffect.forEach(field => {
      field.setAttribute('style', `background: white; white-space: normal; width: auto;`);
    });
    const headerRow = document.querySelector('.transaction_details_row');
    headerRow?.setAttribute('style', 'display: flex; flex-direction: column; gap: 16px;');

    const headerBoxes = document.querySelectorAll('.transaction_detail__box_data');
    const originalHeaderStyles = Array.from(headerBoxes).map(box => box.getAttribute('style') || '');
    headerBoxes.forEach(box => {
      box.setAttribute('style', `width: auto;`);
    });

    const element = document.getElementById('capture') as HTMLElement;
    const canvas = await html2canvas(element, {
      scale: 2,
      ignoreElements: element => {
        return element.classList.contains('no_export');
      },
    });
    headerRow?.setAttribute('style', '');
    fieldsWithEffect.forEach((field, index) => {
      field.setAttribute('style', originalStyles[index] || '');
    });
    headerBoxes.forEach((box, index) => {
      box.setAttribute('style', originalHeaderStyles[index] || '');
    });
    const imgData = canvas.toDataURL('image/png');

    const pdf = new jsPDF('p', 'mm', 'a4');
    const width = pdf.internal.pageSize.getWidth();
    const height = (canvas.height * width) / canvas.width;

    pdf.addImage(imgData, 'PNG', 0, 0, width, height);
    pdf.save('transaction-voucher.pdf');
  };

  const isShopify = data && [SHOPIFY, SHOPIFY_TRANSPARENT].includes(data.paymentSolutionType);
  const isRefunded = Boolean(data?.refunded);
  const hasChargeback = Boolean(data?.chargeback?.externalId);

  const INACTIVE_REFUND_STATUSES = ['CANCELLED', 'REJECTED', 'DECLINED'];
  const totalRefundedAmount = (data?.relatedRefunds || [])
    .filter(r => !INACTIVE_REFUND_STATUSES.includes(r.status))
    .reduce((sum, r) => sum + (r.localAmount || 0), 0);
  const isFullyRefunded = isRefunded && totalRefundedAmount >= (data?.localAmount || 0);
  const canRequestAdditionalRefund = isRefunded && !isFullyRefunded;
  const formattedEciCode =
    data?.errorLogEciCode !== undefined && data?.errorLogEciCode !== null
      ? String(data.errorLogEciCode).padStart(2, '0')
      : null;
  const eciTooltipContent = formattedEciCode ? getEciTooltipContent(formattedEciCode) : null;
  const eciBrandResult = data ? getEciBrandResult(data.checkoutPaymentMethodCode, data.errorLogEciCode) : null;
  const showRejectionReasonIcon = data?.status === PaymentStatus.CANCELLED && eciBrandResult === ECI_RESULT.SUCCESS;
  const showReviewStatusForTxDetails =
    data?.status === PaymentStatus.CANCELLED && eciBrandResult === ECI_RESULT.SUCCESS;
  const statusDetails = data
    ? getStatusDetails(data.status, isRefunded, showReviewStatusForTxDetails, eciBrandResult)
    : '';
  const nextAction = data ? getNextAction(data.endUserErrorType) : '';
  const needsAttention = data ? getNeedsAttention(data.endUserErrorType) : '';
  const isDeclinedTransaction =
    !isRefunded &&
    (showReviewStatusForTxDetails ||
      data?.status === PaymentStatus.DECLINED ||
      (!data?.country && data?.status !== PaymentStatus.COMPLETED));

  const transactionDetailsSection = data ? (
    <TransactionDetailSection
      title={l('transactions.details')}
      icon={<DollarIcon className="transaction_detail__title_section__icon" aria-hidden />}
      defaultExpanded
    >
      <div className="transaction_detail__details-grid">
        <div className="transaction_detail__details-grid__row">
          <div className="transaction_detail__details-grid__field">
            <div className="transaction_detail__table_detail__row__label">
              {l('transactions.details.statusDetails')}:
            </div>
            <div className="transaction_detail__table_detail__row__value">
              {isDeclinedTransaction
                ? l('transactions.details.statusDetails.declinedFixable')
                : statusDetails}
            </div>
          </div>
          <div className="transaction_detail__details-grid__field">
            <div className="transaction_detail__table_detail__row__label">
              {l('transactions.paymentSolutionType')}:
            </div>
            <div className="transaction_detail__table_detail__row__value">
              {l(`transactions.paymentSolutionType.${data.paymentSolutionType}`)}
            </div>
          </div>
        </div>
        <div className="transaction_detail__details-grid__row">
          <div className="transaction_detail__details-grid__field">
            <div className="transaction_detail__table_detail__row__label">{l('transactions.description')}:</div>
            <div className="transaction_detail__table_detail__row__value">{data.description || '-'}</div>
          </div>
          <div className="transaction_detail__details-grid__field">
            <div className="transaction_detail__table_detail__row__label">
              {l('transactions.details.nextAction')}:
            </div>
            <div className="transaction_detail__table_detail__row__value">
              {isDeclinedTransaction
                ? l('transactions.details.nextAction.checkHelpCenter')
                : nextAction}
            </div>
          </div>
        </div>
        {isDeclinedTransaction && (
          <div className="transaction_detail__details-grid__row">
            <div className="transaction_detail__details-grid__field">
              <div className="transaction_detail__table_detail__row__label">
                {l('transactions.details.needsAttention')}:
              </div>
              <div className="transaction_detail__table_detail__row__value">{needsAttention}</div>
            </div>
            <div className="transaction_detail__details-grid__field transaction_detail__details-grid__field--action">
              <div className="transaction_detail__table_detail__row__label" aria-hidden />
              <div className="transaction_detail__table_detail__row__value">
                <Button
                  textTransformNone
                  size="small"
                  variant="outlined"
                  color="primary"
                  className="transaction_detail__declined-help-btn no_export"
                  startIcon={
                    <DeclinedWarningIcon
                      className="transaction_detail__declined-help-btn__icon"
                      aria-hidden
                    />
                  }
                  onClick={() => openNewTabAndFocus(getDeclinedHelpUrl())}
                >
                  {l('checkout-logs.tooltip.help')}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </TransactionDetailSection>
  ) : null;

  return (
    <div className="transactions">
      <TopDashboardSection
        title={l('transactions.details')}
        componentBottom={
          <div className="transaction_detail__back_btn">
            <ButtonLink
              type="secondary"
              text={
                <>
                  <BackIcon /> {l('transactions.details.backBtn')}
                </>
              }
              onClick={() => history.push(Routes.TRANSACTIONS)}
            />
          </div>
        }
      />
      <div className="transaction_detail">
        {/* <div className="transaction_detail__export_btn">
          <Button
            textTransformNone
            startIcon={<FileDownload />}
            size="small"
            style={{ backgroundColor: '#F5F5F5' }}
            color="primary"
            variant="outlined"
            disabled={isShopify}
            onClick={exportToPDF}
          >
            Export to PDF
          </Button>
        </div> */}
        <div id="capture">
          <Card>
            {loading && <Loading />}
            {!loading && data && error && <MessageError errorInfo={errorInfo} />}
            {!loading && data && !error && (
              <>
                <div className="transaction_detail__card_section">
                  <div className="transaction_detail__amount_paid">
                    <div className="transaction_detail__amount_paid__title">{l('transactions.amountPaid')}</div>
                    <div className="transaction_detail__amount_paid__value">
                      {data.currency} {data.localAmount}
                    </div>
                  </div>
                  <div className="transaction_detail__status">
                    <div className="transaction_detail__status__title">{l('transactions.status')}</div>
                    <div className="transaction_detail__status__value">
                      {data.refunded ? (
                        <ChipStatus
                            status="refunded"
                            text={l('transactions.status.REFUNDED')}

                        />
                      ) : (
                        <ChipStatus
                            status={data.status}
                            text={l(`transactions.status.${data.status}`)}
                            endIcon={
                              showRejectionReasonIcon ? (
                                  <InfoIcon onClick={() => setShowRejectionReasonModal(true)} />
                              ) : undefined
                            }
                        />

                      )}
                    </div>
                  </div>
                  <div className="transaction_detail__divider" />
                  <div className="transaction_details_row">
                    <div className="transaction_details_row__pair">
                      <div className="transaction_detail__box_data">
                        <div className="transaction_detail__box_data__title">{l('transactions.createDate')}</div>
                        <div className="transaction_detail__box_data__value">
                          <Moment format="DD/MM/YYYY, HH:mm a">{createDate(data.creationDate)}</Moment>
                        </div>
                      </div>
                      <div className="transaction_detail__box_data transaction_detail__box_data--right">
                        <div className="transaction_detail__box_data__title">{l('transactions.details.orderId')}</div>
                        <div className="transaction_detail__box_data__value transaction_detail__box_data__value--right">
                          <span className="transaction_detail__box_data__value__text">{data.invoiceId}</span>
                          <CopyToClipboard
                            text={data.invoiceId}
                            onCopy={() => {
                              segmentTransactionsTransactionDetailCopyInvoiceId({
                                link: data.invoiceId,
                              });
                              setWasCopied(true);
                            }}
                          >
                            <Tooltip
                              onMouseEnter={() => setWasCopied(false)}
                              title={l(wasCopied ? 'common.copied' : 'common.copy')}
                            >
                              <CopyIcon className="transaction_detail__box_data__value__icon no_export" />
                            </Tooltip>
                          </CopyToClipboard>
                        </div>
                      </div>
                    </div>
                    <div className="transaction_details_row__pair">
                      <div className="transaction_detail__box_data">
                        <div className="transaction_detail__box_data__title">{l('transactions.details.paymentDate')}</div>
                        <div className="transaction_detail__box_data__value">
                          {data.status === PaymentStatus.COMPLETED || isRefunded ? (
                            <Moment format="DD/MM/YYYY">{createDate(data.creationDate)}</Moment>
                          ) : (
                            <span>---</span>
                          )}
                        </div>
                      </div>
                      <div className="transaction_detail__box_data transaction_detail__box_data--right">
                        <div className="transaction_detail__box_data__title">{l('transactions.id')}</div>
                        <div className="transaction_detail__box_data__value transaction_detail__box_data__value--right">
                          {data.externalId ? (
                            <>
                              <span className="transaction_detail__box_data__value__text">{data.externalId}</span>
                              <CopyToClipboard
                                text={data.externalId}
                                onCopy={() => {
                                  segmentTransactionsTransactionDetailCopyExternalId({
                                    link: data.externalId,
                                  });
                                  setWasCopied(true);
                                }}
                              >
                                <Tooltip
                                  onMouseEnter={() => setWasCopied(false)}
                                  title={l(wasCopied ? 'common.copied' : 'common.copy')}
                                >
                                  <CopyIcon className="transaction_detail__box_data__value__icon no_export" />
                                </Tooltip>
                              </CopyToClipboard>
                            </>
                          ) : (
                            <span>-</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {isDeclinedTransaction && transactionDetailsSection}
                <TransactionDetailSection
                  title={l('transactions.paymentDetails')}
                  icon={<CardIcon className="transaction_detail__title_section__icon" aria-hidden />}
                  defaultExpanded
                  headerActions={
                    data.status === PaymentStatus.COMPLETED
                      ? isRefunded && !canRequestAdditionalRefund
                        ? (
                            <Button
                              startIcon={<CheckIcon />}
                              size="small"
                              variant="outlined"
                              disabled
                              className="transaction_detail__refund-btn transaction_detail__refund-btn--refunded"
                            >
                              {l('transactions.refunded')}
                            </Button>
                          )
                        : isShopify
                          ? (
                              <Tooltip
                                title={Parser(l('transactions.details.shopifyRefund.title'))}
                                classes={{
                                  tooltip: 'bank_accounts__tooltip',
                                  arrow: 'bank_accounts__tooltip__arrow',
                                }}
                                placement="left-start"
                                enterTouchDelay={0}
                              >
                                <span>
                                  <Button
                                    startIcon={<RefundsIcon />}
                                    size="small"
                                    variant="outlined"
                                    disabled
                                  >
                                    {l('transactions.addRefund')}
                                  </Button>
                                </span>
                              </Tooltip>
                            )
                          : hasChargeback
                            ? (
                                <Tooltip
                                  title={l('msgError.requestRefund.chargebackInPlace')}
                                  classes={{
                                    tooltip: 'bank_accounts__tooltip',
                                    arrow: 'bank_accounts__tooltip__arrow',
                                  }}
                                  placement="left-start"
                                  enterTouchDelay={0}
                                >
                                  <span>
                                    <Button
                                      startIcon={<RefundsIcon />}
                                      size="small"
                                      variant="outlined"
                                      disabled
                                    >
                                      {l('transactions.addRefund')}
                                    </Button>
                                  </span>
                                </Tooltip>
                              )
                            : (
                              <Button
                                startIcon={<RefundsIcon />}
                                size="small"
                                variant="outlined"
                                disabled={!canRefund}
                                onClick={() => {
                                  setTransactionToRefund(data);
                                }}
                              >
                                {l('transactions.addRefund')}
                              </Button>
                            )
                      : undefined
                  }
                >
                  <div className="transaction_detail__data_section">
                      <div className="transaction_detail__box_detail">
                        <div className="transaction_detail__box_detail__row">
                          <div className="transaction_detail__box_detail__row__label">
                            <div className="transaction_detail__box_detail__row__label__text">
                              {l('transactions.amountPaid')}
                            </div>
                            <TooltipIcon
                              placement="bottom"
                              text={l(
                                'transactions.details.amountPaid.tooltip',
                                data.currency,
                                (functionalCurrenciesByCountry as { [key: string]: string })[merchantMe?.country || ''] ||
                                  '***'
                              )}
                              icon={<InfoIcon />}
                            />
                          </div>
                          <div className="transaction_detail__box_detail__row__value">
                            {data.currency} {data.localAmount}
                          </div>
                        </div>
                        {data.installmentsFeeAmount && (
                          <div className="transaction_detail__box_detail__row">
                            <div className="transaction_detail__box_detail__row__label">
                              <div className="transaction_detail__box_detail__row__label__text">
                                {l('transactions.installmentsFeeAmount')}
                              </div>
                              <TooltipIcon
                                placement="bottom"
                                text={l('transactions.details.installmentsFeeAmount.tooltip')}
                                icon={<InfoIcon />}
                              />
                            </div>
                            <div className="transaction_detail__box_detail__row__value">
                              {data.balanceCurrency} {data.installmentsFeeAmount}
                            </div>
                          </div>
                        )}
                        <div className="transaction_detail__box_detail__row">
                          <div className="transaction_detail__box_detail__row__label">
                            <div className="transaction_detail__box_detail__row__label__text">
                              {l('transactions.processingFee')}
                            </div>
                            <TooltipIcon
                              placement="bottom"
                              text={l('transactions.details.processingFee.tooltip')}
                              icon={<InfoIcon />}
                            />
                          </div>
                          <div className="transaction_detail__box_detail__row__value">
                            {data.balanceCurrency} {data.balanceFee}
                          </div>
                        </div>
                        {data.collaboratorPayedCommission && (
                          <div className="transaction_detail__box_detail__row">
                            <div className="transaction_detail__box_detail__row__label">
                              <div className="transaction_detail__box_detail__row__label__text">
                                {l('transactions.collaboratorPayedCommission')}
                              </div>
                              <TooltipIcon
                                placement="bottom"
                                text={l('transactions.details.collaboratorFee.tooltip')}
                                icon={<InfoIcon />}
                              />
                            </div>
                            <div className="transaction_detail__box_detail__row__value">
                              {data.balanceCurrency} {data.collaboratorPayedCommission * -1}
                            </div>
                          </div>
                        )}
                        {!!data.threeDSFeesAmount && (
                          <div className="transaction_detail__box_detail__row">
                            <div className="transaction_detail__box_detail__row__label">
                              <div className="transaction_detail__box_detail__row__label__text">
                                {l('transactions.threeDSFeesAmount', data.threeDSAttempts)}
                              </div>
                              <TooltipIcon
                                placement="bottom"
                                text={l('transactions.details.threeDSFeesAmount.tooltip')}
                                icon={<InfoIcon />}
                              />
                            </div>
                            <div className="transaction_detail__box_detail__row__value">
                              {data.balanceCurrency} {data.threeDSFeesAmount * -1}
                            </div>
                          </div>
                        )}
                        {isCrossBorder && (
                          <div className="transaction_detail__box_detail__row">
                            <div className="transaction_detail__box_detail__row__label">
                              {l('transactions.exchangeRate')}
                            </div>
                            <div className="transaction_detail__box_detail__row__value">
                              USD 1 <ArrowRightIcon /> {data.currency} {data.usdFxRate.toFixed(2)}
                            </div>
                          </div>
                        )}
                        <div className="transaction_detail__box_detail__divider" />
                        {isCrossBorder && data.balanceCurrency !== 'USD' && (
                          <div className="transaction_detail__box_detail__row">
                            <div className="transaction_detail__box_detail__row__label_bottom">
                              {l('transactions.amountInUSD')}
                            </div>
                            <div className="transaction_detail__box_detail__row__value_bottom">USD {data.usdAmount}</div>
                          </div>
                        )}
                        <div className="transaction_detail__box_detail__row">
                          <div className="transaction_detail__box_detail__row__label_bottom">
                            {l('transactions.amountReceived')}
                          </div>
                          <div className="transaction_detail__box_detail__row__value_bottom">
                            {data.balanceCurrency} {data.balanceAmount + (data.collaboratorPayedCommission || 0)}
                          </div>
                        </div>
                      </div>

                      <div className="transaction_detail__table_detail transaction_detail__column_right">
                        <div className="transaction_detail__table_detail__row">
                          <div className="transaction_detail__table_detail__row__label">{l('transactions.country')}</div>
                          <div className="transaction_detail__table_detail__row__value">
                            <img
                              alt=""
                              className="transaction_detail__country__flag"
                              src={`${Urls.roundedFlags.replace(':code', getMappedCountryCode(data.country))}`}
                            />
                            <span>&nbsp;{l(`countryCode.${data.country}`)}</span>
                          </div>
                        </div>
                        <div className="transaction_detail__table_detail__row">
                          <div className="transaction_detail__table_detail__row__label">
                            {l('transactions.paymentMethod')}
                          </div>
                          <div className="transaction_detail__table_detail__row__value">
                            {data.paymentMethod}
                            {PaymentMethodTypes.CREDIT_CARD === data.paymentMethodType && data.ccLastDigits && (
                              <span>&nbsp;({data.ccLastDigits}) </span>
                            )}
                          </div>
                        </div>
                        {PaymentMethodTypes.CREDIT_CARD === data.paymentMethodType && (
                          <div className="transaction_detail__table_detail__row">
                            <div className="transaction_detail__table_detail__row__label">
                              {l(`transactions.installments${merchantMe?.country === 'MX' ? '.MX' : ''}`)}
                            </div>
                            <div className="transaction_detail__table_detail__row__value">{data.installments || '-'}</div>
                          </div>
                        )}
                        {data.txEmail && (
                          <div className="transaction_detail__table_detail__row">
                            <div className="transaction_detail__table_detail__row__label">{l('transactions.txEmail')}</div>
                            <div className="transaction_detail__table_detail__row__value">{data.txEmail}</div>
                          </div>
                        )}
                        {eciBrandResult && (
                          <div className="transaction_detail__table_detail__row">
                            <div className="transaction_detail__table_detail__row__label">
                              {l('transactions.eciCode')}
                              {eciTooltipContent && (
                                <TooltipIcon
                                  placement="bottom"
                                  text={eciTooltipContent}
                                  icon={<InfoIcon />}
                                  reducedPadding
                                />
                              )}
                            </div>
                            <div className="transaction_detail__table_detail__row__value">
                              {eciBrandResult === ECI_RESULT.SUCCESS ? (
                                <CheckIcon className="transaction_detail__eci_result_icon transaction_detail__eci_result_icon__success" />
                              ) : eciBrandResult === ECI_RESULT.FAILURE ? (
                                <CancelIcon className="transaction_detail__eci_result_icon transaction_detail__eci_result_icon__error" />
                              ) : null}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                </TransactionDetailSection>
                {!isDeclinedTransaction && transactionDetailsSection}
                <TransactionDetailSection
                  title={l('transactions.clientInformation')}
                  icon={<UserIcon className="transaction_detail__title_section__icon" aria-hidden />}
                >
                  <div>
                      <div className="transaction_detail__column_left">
                        <div className="transaction_detail__table_detail">
                          <div className="transaction_detail__table_detail__row">
                            <div className="transaction_detail__table_detail__row__label">
                              {l('transactions.clientName')}
                            </div>
                            <div className="transaction_detail__table_detail__row__value">
                              {data.clientFirstName} {data.clientLastName}
                            </div>
                          </div>
                          <div className="transaction_detail__table_detail__row">
                            <div className="transaction_detail__table_detail__row__label">
                              {l('transactions.clientDocument')}
                            </div>
                            <div className="transaction_detail__table_detail__row__value">
                              {data.clientDocumentType} {data.clientDocument}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="transaction_detail__column_right">
                        <div className="transaction_detail__table_detail">
                          <div className="transaction_detail__table_detail__row">
                            <div className="transaction_detail__table_detail__row__label">
                              {l('transactions.clientEmail')}
                            </div>
                            <div className="transaction_detail__table_detail__row__value">{data.clientEmail}</div>
                          </div>
                          <div className="transaction_detail__table_detail__row">
                            <div className="transaction_detail__table_detail__row__label">{l('transactions.clientId')}</div>
                            <div className="transaction_detail__table_detail__row__value">{data.clientId}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                </TransactionDetailSection>
                {data.preChargebackForm?.comments && (
                  <TransactionDetailSection
                    title={l('transactions.chargebackform')}
                    icon={<UserIcon className="transaction_detail__title_section__icon" aria-hidden />}
                  >
                    <div>
                        <div className="transaction_detail__column_left">
                          <div className="transaction_detail__table_detail">
                            <div className="transaction_detail__table_detail__row">
                              <div className="transaction_detail__table_detail__row__label">
                                {l('transactions.chargebackform.product')}
                              </div>
                              <div className="transaction_detail__table_detail__row__value">
                                {data.preChargebackForm.product}
                              </div>
                            </div>
                            <div className="transaction_detail__table_detail__row">
                              <div className="transaction_detail__table_detail__row__label">
                                {l('transactions.chargebackform.problem')}
                              </div>
                              <div className="transaction_detail__table_detail__row__value">
                                {data.preChargebackForm.problem}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="transaction_detail__column_right">
                          <div className="transaction_detail__table_detail">
                            <div className="transaction_detail__table_detail__row">
                              <div className="transaction_detail__table_detail__row__label">
                                {l('transactions.chargebackform.comments')}
                              </div>
                              <div className="transaction_detail__table_detail__row__value">
                                {data.preChargebackForm.comments}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                  </TransactionDetailSection>
                )}
                {data.chargeback.externalId && (
                  <TransactionDetailSection
                    title={l('transactions.chargeback.title')}
                    icon={<CardIcon className="transaction_detail__title_section__icon" aria-hidden />}
                  >
                    <div>
                        <div className="transaction_detail__column_left">
                          <div className="transaction_detail__table_detail">
                            <div className="transaction_detail__table_detail__row">
                              <div className="transaction_detail__table_detail__row__label">
                                {l('chargebacks.reference')}
                              </div>
                              <div className="transaction_detail__table_detail__row__value">
                                <CopyToClipboard text={data.chargeback.externalId} onCopy={() => setWasCopied(true)}>
                                  <Tooltip
                                    onMouseEnter={() => setWasCopied(false)}
                                    title={l(wasCopied ? 'common.copied' : 'common.copy')}
                                  >
                                    <span className="transaction_detail__chargeback_reference">
                                      <span>{data.chargeback.externalId}</span>
                                      <CopyIcon className="no_export" />
                                    </span>
                                  </Tooltip>
                                </CopyToClipboard>
                              </div>
                            </div>
                            <div className="transaction_detail__table_detail__row">
                              <div className="transaction_detail__table_detail__row__label">{l('chargebacks.amount')}</div>
                              <div className="transaction_detail__table_detail__row__value">
                                {data.currency} {data.localAmount}
                              </div>
                            </div>
                            <div className="transaction_detail__table_detail__row">
                              <div className="transaction_detail__table_detail__row__label">
                                {l('chargebacks.createdDate')}
                              </div>
                              <div className="transaction_detail__table_detail__row__value">
                                <Moment format="DD/MM/YYYY">{createDate(data.chargeback.date)}</Moment>
                                <span>, </span>
                                <Moment format="hh:mm:ss a">{createDate(data.chargeback.date)}</Moment>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="transaction_detail__column_right">
                          <div className="transaction_detail__table_detail">
                            <div className="transaction_detail__table_detail__row">
                              <div className="transaction_detail__table_detail__row__label">
                                {l('transactions.status')}
                              </div>
                              <div className="transaction_detail__table_detail__row__value">
                                <ChipStatus
                                  status={data.chargeback.status?.toLowerCase() ?? 'pending'}
                                  text={l(`chargebacks.status.${data.chargeback.status}`)}
                                />
                              </div>
                            </div>
                            <div className="transaction_detail__table_detail__row">
                              <div className="transaction_detail__table_detail__row__label">
                                {l('chargebacks.reasonDescription')}
                              </div>
                              <div className="transaction_detail__table_detail__row__value">
                                {data.chargeback.reasonDescription || ' - '}
                              </div>
                            </div>
                            <div className="transaction_detail__table_detail__row">
                              <div className="transaction_detail__table_detail__row__label">
                                {l('chargebacks.resolution')}
                              </div>
                              <div className="transaction_detail__table_detail__row__value">
                                <ChargebackResolution resolution={data.chargeback.resolution} />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                  </TransactionDetailSection>
                )}
                {data.relatedRefunds && data.relatedRefunds.length > 0 && (
                  <TransactionDetailSection
                    title={l('transactions.relatedTransactions')}
                    icon={<LinkIcon className="transaction_detail__title_section__icon" aria-hidden />}
                  >
                    {data.relatedRefunds.map(refund => (
                      <div key={refund.id}>
                        <div className="transaction_detail__refund_subtitle">
                          <RefundsIcon className="transaction_detail__refund_subtitle__icon" aria-hidden />
                          <span>{l('transactions.refund')}</span>
                        </div>
                        <div className="transaction_detail__details-grid">
                          <div className="transaction_detail__details-grid__row">
                            <div className="transaction_detail__details-grid__field">
                              <div className="transaction_detail__table_detail__row__label">
                                {l('chargebacks.reference')}
                              </div>
                              <div className="transaction_detail__table_detail__row__value">
                                <CopyToClipboard text={String(refund.id)} onCopy={() => setWasCopied(true)}>
                                  <Tooltip
                                    onMouseEnter={() => setWasCopied(false)}
                                    title={l(wasCopied ? 'common.copied' : 'common.copy')}
                                  >
                                    <span className="transaction_detail__chargeback_reference">
                                      <span>{refund.id}</span>
                                      <CopyIcon className="no_export" />
                                    </span>
                                  </Tooltip>
                                </CopyToClipboard>
                              </div>
                            </div>
                            <div className="transaction_detail__details-grid__field">
                              <div className="transaction_detail__table_detail__row__label">
                                {l('transactions.status')}
                              </div>
                              <div className="transaction_detail__table_detail__row__value">
                                <ChipStatus
                                  status={refund.status?.toLowerCase() ?? 'pending'}
                                  text={l(`refunds.status.${refund.status}`)}
                                />
                              </div>
                            </div>
                          </div>
                          <div className="transaction_detail__details-grid__row">
                            <div className="transaction_detail__details-grid__field">
                              <div className="transaction_detail__table_detail__row__label">
                                {l('transactions.amountPaid')}
                              </div>
                              <div className="transaction_detail__table_detail__row__value">
                                {refund.currency} {refund.localAmount}
                              </div>
                            </div>
                            <div className="transaction_detail__details-grid__field">
                              <div className="transaction_detail__table_detail__row__label">
                                {l('transactions.paymentMethod')}
                              </div>
                              <div className="transaction_detail__table_detail__row__value">
                                {refund.paymentMethod || '-'}
                              </div>
                            </div>
                          </div>
                          <div className="transaction_detail__details-grid__row">
                            <div className="transaction_detail__details-grid__field">
                              <div className="transaction_detail__table_detail__row__label">
                                {l('chargebacks.createdDate')}
                              </div>
                              <div className="transaction_detail__table_detail__row__value">
                                <Moment format="DD/MM/YYYY, h:mm:ss a">{createDate(refund.date)}</Moment>
                                <span> (GMT +0)</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </TransactionDetailSection>
                )}
              </>
            )}
          </Card>
        </div>
        {transactionToRefund && (
          <Dialog
            dividers={false}
            unpaddingContent
            fullScreenMobile
            type={transactionToRefund.paymentMethodType === PaymentMethodTypes.CREDIT_CARD ? 'sm' : 'md'}
            title={l('addRefund.title')}
            body={
              <AddRefund
                beneficiary={`${transactionToRefund.clientFirstName} ${transactionToRefund.clientLastName}`.trim()}
                country={transactionToRefund.country}
                transactionId={transactionToRefund.id}
                invoiceId={transactionToRefund.invoiceId}
                currency={transactionToRefund.currency}
                amount={transactionToRefund.localAmount}
                paymentMethodType={transactionToRefund.paymentMethodType}
              />
            }
            handleClose={() => {
              setTransactionToRefund(null);
              if (requestRefundSucess) {
                history.push(Routes.REFUNDS);
              }
            }}
          />
        )}
        {showRejectionReasonModal && data && (
          <RejectionReasonModal
            endUserErrorType={data.endUserErrorType}
            onClose={() => setShowRejectionReasonModal(false)}
          />
        )}
      </div>
    </div>
  );
};

export default TransactionDetail;
